import {
	Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
	FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, TextField, Box
} from "@mui/material";
import { addScope } from "api/layer";
import { AppAction, AppContext } from "helpers/context";
import { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./add-modal.module.scss";

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	data?: FormData;
	groupData: any[];
}

interface FormData {
	id?: string;
	scopeName: string;
	groupId: string;
	layerIds: any;
	remark: string;
}

function AddModal(props: Props) {
	const [t] = useTranslation();
	const [context, dispatch] = useContext(AppContext);
	const [formData, setFormData] = useState<FormData>(
		props.data
			? {
				...props.data,
				layerIds: (() => {
					if (!props.data.layerIds) return [];
					if (typeof props.data.layerIds === 'string') {
						return props.data.layerIds.split(',');
					}
					return Array.isArray(props.data.layerIds) ? props.data.layerIds : [];
				})()
			}
			: {
				scopeName: "",
				groupId: "",
				layerIds: [],
				remark: ""
			}
	);
	const [isError, setIsError] = useState(false);
	const hazardLayer = context.layerData || [];
	const climateLayer = [
		...(context.crsLayerList || []),
		{
			"id": "drought_old",
			"name": "Drought Score (old)",
			"description": "",
			"alias": "drought_old"
		},
		{
			"id": "wet",
			"name": "Extreme Precipitation Score (old)",
			"description": "",
			"alias": "wet"
		},
		{
			"id": "boreal_winter_wet",
			"name": "winter (November – March) Precipitation",
			"description": "",
			"alias": "boreal_winter_wet"
		},
		{
			"id": "boreal_summer_wet",
			"name": "Summer (April – October) Precipitation",
			"description": "",
			"alias": "boreal_summer_wet"
		}
	];
	const eventLayer = [
		{
			id: 'EVENTS_CYCLONE_EVEFG',
			name: 'EVENTS_CYCLONE_EVEFG',
		}, {
			id: 'EVENTS_WINTERSTORM_EVEFG',
			name: 'EVENTS_WINTERSTORM_EVEFG',
		}, {
			id: 'EVENTS_EARTHQUAKE_EVEFG',
			name: "EVENTS_EARTHQUAKE_EVEFG"
		}, {
			id: 'EVENTS_FLOOD_ICEYE',
			name: 'EVENTS_FLOOD_ICEYE',
		},
		{
			id: "EVENTS_RAINFALL_EVEFG",
			name: "EVENTS_RAINFALL_EVEFG"
		}
	];
	const futureHazardLayer = [{
		id: "futureHazard.drought",
		name: "Drought Score"
	}, {
		id: "futureHazard.extreme_precipitation",
		name: "Extreme Precipitation Score"
	}, {
		id: "futureHazard.fluvial_flood",
		name: "Fluvial Flood Score"
	}, {
		id: "futureHazard.pluvial_flood",
		name: "Pluvial Flood Score"
	}, {
		id: "futureHazard.heat_wave",
		name: "Heat Wave Score"
	}, {
		id: "futureHazard.windstorm",
		name: "Windstorm Score"
	}, {
		id: "futureHazard.storm_surge",
		name: "Storm Surge (Sea Level Rise)"
	}, {
		id: "futureHazard.wildfire",
		name: "Wildfire"
	}, {
		id: "futureHazard.heat_stress",
		name: "Heat Stress"
	}];
	const CRSComponentLayer = [{
		id: "CRSComponent.temp_mean_annual",
		name: "Mean annual temperature(℃)"
	}, {
		id: "CRSComponent.temp_extreme_24h",
		name: "Extreme 24-hour mean temperature(℃)"
	}, {
		id: "CRSComponent.drought_annual_spei_min",
		name: "Minimum of Annual Standardized Precipitation Evapotranspiration Index (SPEI)"
	}, {
		id: "CRSComponent.wind_extreme_99th_percentile",
		name: "99th percentile wind speed(m/s)"
	}, {
		id: "CRSComponent.sea_level_rise",
		name: "Sea level rise(m)"
	}, {
		id: "CRSComponent.precip_extreme_3_day_99th",
		name: "Extreme 3-day precipitation change(mm)"
	}, {
		id: "CRSComponent.precip_annual_accumulated",
		name: "Annual accumulated precipitation(mm)"
	}, {
		id: "CRSComponent.heat_stress_days_twbg_above_32c",
		name: "Annual days with wet-bulb globe temperature (WBGT) exceeding 32°C(days/year)"
	}, {
		id: "CRSComponent.wildfire_days_fwi_above_21_3",
		name: "Annual days with Fire Weather Index (FWI) > 21.3 (High) (days/year)"
	}];

	const layerGroups = [
		{ key: "hazard", layers: hazardLayer },
		{ key: "climate", layers: climateLayer },
		{ key: "event", layers: eventLayer },
		{ key: "futureHazard", layers: futureHazardLayer },
		{ key: "CRSComponent", layers: CRSComponentLayer }
	];

	const layerList = layerGroups.flatMap((group) => group.layers);

	const getGroupSelectionState = (layers: any[]) => {
		const selectedCount = layers.filter((item: any) => formData.layerIds.includes(item.id)).length;
		return {
			checked: layers.length > 0 && selectedCount === layers.length,
			indeterminate: selectedCount > 0 && selectedCount < layers.length
		};
	};

	const toggleGroupLayers = (layers: any[]) => {
		setFormData((prevData) => {
			const groupLayerIds = layers.map((item: any) => item.id);
			const selectedCount = groupLayerIds.filter((id: string) => prevData.layerIds.includes(id)).length;

			if (selectedCount === groupLayerIds.length) {
				return {
					...prevData,
					layerIds: prevData.layerIds.filter((id: string) => !groupLayerIds.includes(id))
				};
			}

			return {
				...prevData,
				layerIds: Array.from(new Set([...prevData.layerIds, ...groupLayerIds]))
			};
		});
	};

	const toggleSingleLayer = (layerId: string) => {
		setFormData((prevData) => {
			const exists = prevData.layerIds.includes(layerId);
			return {
				...prevData,
				layerIds: exists
					? prevData.layerIds.filter((id: string) => id !== layerId)
					: [...prevData.layerIds, layerId]
			};
		});
	};

	const handleChange = (event: any) => {
		const { name, value, type, checked } = event.target;
		const val = type === "checkbox" ? checked : value;
		if (name === "layerIds") {
			const rawLayerIds = typeof value === "string" ? value.split(",") : value;
			const validLayerIds = rawLayerIds.filter((id: string) => layerList.some((item: any) => item.id === id));
			setFormData((prevData) => ({ ...prevData, [name]: validLayerIds }));
		} else {
			setFormData((prevData) => ({ ...prevData, [name]: val }));
		}
	};

	const close = () => {
		props.onClose();
	};

	const handleSave = () => {
		let data = { ...formData };
		if (!data.scopeName || !data.groupId || !data.layerIds.length) {
			setIsError(true);
			return;
		}
		setIsError(false);

		if (props.data) {
			addScope(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editEndPointScope",
								message: "editEndPointScopeSuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editEndPointScope",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		} else {
			addScope(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addEndPointScope",
								message: "addEndPointScopeSuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addEndPointScope",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		}
	};

	return (
		<Dialog
			open={props.open}
			onClose={close}
		>
			<DialogTitle
				sx={{
					fontWeight: 600,
					fontSize: "16px",
					opacity: 0.79,
					color: "#333",
					width: "400px",
				}}
			>
				{props.data ? t("userSystem.editEndPointScope") : t("userSystem.addEndPointScope")}
			</DialogTitle>
			<DialogContent sx={{ paddingTop: "10px!important" }}>
				<Stack spacing={{ xy: 1, sm: 2 }}>
					<TextField
						required
						name="scopeName"
						value={formData.scopeName}
						error={isError && !formData.scopeName}
						onChange={handleChange}
						label={t("userSystem.scopeName")}
						variant="outlined"
						color="success"
					/>

					<FormControl fullWidth error={isError && !formData.groupId}>
						<InputLabel color="success" required>{t("userSystem.groupName")}</InputLabel>
						<Select
							name="groupId"
							value={formData.groupId}
							onChange={handleChange}
							label={t("userSystem.groupName")}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>

							{props.groupData.map((item) => (<MenuItem value={item.id} key={item.id}>{item.groupName}</MenuItem>))}
						</Select>
					</FormControl>

					<FormControl fullWidth error={isError && !formData.layerIds.length}>
						<InputLabel color="success" required>{t("userSystem.layerNames")}</InputLabel>
						<Select
							name="layerIds"
							value={formData.layerIds}
							multiple
							onChange={handleChange}
							label={t("userSystem.layerNames")}
							renderValue={(selected) => layerList.filter((item: any) => selected.includes(item.id))
								.map((item: any) => t("layerId." + item.id)).join(',')}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{layerGroups.map((group) => {
								const selectionState = getGroupSelectionState(group.layers);
								return (
									<Fragment key={group.key}>
										<MenuItem
											value={group.key}
											onClick={(event) => {
												event.preventDefault();
												event.stopPropagation();
												toggleGroupLayers(group.layers);
											}}
											style={{
												backgroundColor: selectionState.checked || selectionState.indeterminate ? "#0e8a3f14" : "transparent"
											}}
										>
											<Checkbox
												checked={selectionState.checked}
												indeterminate={selectionState.indeterminate}
												color="success"
											/>
											<ListItemText primary={t("options." + group.key)} />
										</MenuItem>
										{group.layers.map((item: any) => (
											<MenuItem
												value={item.id}
												key={item.id}
												onClick={(event) => {
													event.preventDefault();
													event.stopPropagation();
													toggleSingleLayer(item.id);
												}}
												style={{
													paddingLeft: "30px"
												}}
											>
												<Checkbox checked={formData.layerIds.includes(item.id)} color="success" />
												<ListItemText primary={t("layerId." + item.id)} />
											</MenuItem>
										))}
									</Fragment>
								);
							})}
						</Select>
					</FormControl>

					<TextField
						name="remark"
						value={formData.remark}
						onChange={handleChange}
						label={t("userSystem.description")}
						variant="outlined"
						color="success"
					/>

				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={close}
					color="success"
				>
					{t("buttons.cancel")}
				</Button>
				<Button
					onClick={handleSave}
					color="success"
					variant="contained"
				>
					{t("buttons.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default AddModal;
